import { Controller } from "@hotwired/stimulus"

// Floods the section with inbox cards cloned from a <template> pool, at random
// positions that bleed off every edge but avoid a generous center rectangle.
// On entering the viewport the intro rises in and the cards pop in rapidly,
// then a rAF loop gives each a gentle bob AND a cursor repel — moving the mouse
// shoves nearby cards away (with spring-back), like clearing the clutter.
// Replays on re-entry. Static for reduced motion; desktop only.
export default class extends Controller {
  static targets = ["source", "field", "intro"]
  static values = { count: { type: Number, default: 80 } }

  CARD_W = 22
  CARD_H = 9
  CENTER = { x0: 24, x1: 76, y0: 18, y1: 76 }
  REPEL_RADIUS = 340
  REPEL_STRENGTH = 220

  connect() {
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.desktop = window.matchMedia("(min-width: 1024px)").matches
    // Fewer cards on low-core machines to keep the flood light.
    this.maxCards = (navigator.hardwareConcurrency || 8) <= 4 ? Math.min(this.countValue, 40) : this.countValue

    if (this.reduced) {
      if (this.desktop) this.generate()
      return
    }

    this.introTargets.forEach((intro) => (intro.style.opacity = "0"))
    this.active = false
    this.introRevealed = false
    this.mouse = { x: -9999, y: -9999, active: false }
    this.onMove = this.onMove.bind(this)
    this.onLeave = () => (this.mouse.active = false)
    this.frame = this.frame.bind(this)

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting)
        if (visible && !this.active) {
          this.activate()
        } else if (!visible && this.active) {
          this.deactivate()
        }
      },
      { threshold: 0.15 }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    this.deactivate()
    this.fieldTarget.replaceChildren()
  }

  activate() {
    this.active = true

    if (!this.introRevealed) {
      this.introRevealed = true
      this.introTargets.forEach((intro) => {
        intro.animate(
          [{ opacity: 0, transform: "translateY(12px)" }, { opacity: 1, transform: "translateY(0)" }],
          { duration: 600, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "both" }
        )
      })
    }

    if (!this.desktop) return
    this.cards = this.generate() // fresh flood each entry (clears the previous)
    this.cards.forEach((card, i) => {
      const pop = card.el.animate(
        [{ opacity: 0, transform: "scale(0)" }, { opacity: 1, transform: "scale(1)" }],
        { duration: 320, delay: i * 22, easing: "cubic-bezier(0.34, 1.56, 0.64, 1)", fill: "both" }
      )
      pop.onfinish = () => {
        card.el.style.opacity = "1"
        pop.cancel()
        card.ready = true
      }
    })

    this.element.addEventListener("mousemove", this.onMove)
    this.element.addEventListener("mouseleave", this.onLeave)
    this.rafId = requestAnimationFrame(this.frame)
  }

  // Pause when out of view (keeps the headline + cards in place); re-entry
  // replays from activate().
  deactivate() {
    this.active = false
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = null
    this.element.removeEventListener("mousemove", this.onMove)
    this.element.removeEventListener("mouseleave", this.onLeave)
  }

  onMove(event) {
    const rect = this.element.getBoundingClientRect()
    this.mouse.x = event.clientX - rect.left
    this.mouse.y = event.clientY - rect.top
    this.mouse.active = true
  }

  generate() {
    const sources = Array.from(this.sourceTarget.content.children)
    if (sources.length === 0) return []

    this.fieldTarget.replaceChildren()
    const fragment = document.createDocumentFragment()
    const cards = []
    for (let n = 0; n < this.maxCards; n++) {
      const outer = sources[Math.floor(Math.random() * sources.length)].cloneNode(true)
      const [x, y] = this.position()
      outer.style.left = `${x}%`
      outer.style.top = `${y}%`
      outer.style.transform = `rotate(${(Math.random() * 12 - 6).toFixed(2)}deg)`

      const inner = outer.firstElementChild
      if (!this.reduced) inner.style.opacity = "0"
      fragment.appendChild(outer)
      cards.push({ el: inner, outer })
    }
    this.fieldTarget.appendChild(fragment)

    cards.forEach((card) => {
      card.hx = card.outer.offsetLeft + card.outer.offsetWidth / 2
      card.hy = card.outer.offsetTop + card.outer.offsetHeight / 2
      card.ox = 0
      card.oy = 0
      card.ready = this.reduced
    })
    return cards
  }

  // Repel-only loop. Runs while the section is in view (stopped entirely when it
  // scrolls off — see deactivate). The cost that matters, the style write, is
  // skipped whenever a card hasn't actually moved, so an idle on-screen section
  // is just cheap math with zero DOM writes. No perpetual float.
  frame() {
    const radius = this.REPEL_RADIUS
    const strength = this.REPEL_STRENGTH
    const mouse = this.mouse

    for (const card of this.cards) {
      if (!card.ready) continue

      let tx = 0
      let ty = 0
      if (mouse.active) {
        const dx = card.hx - mouse.x
        const dy = card.hy - mouse.y
        const dist = Math.hypot(dx, dy) || 1
        if (dist < radius) {
          const force = (1 - dist / radius) * strength
          tx = (dx / dist) * force
          ty = (dy / dist) * force
        }
      }

      card.ox += (tx - card.ox) * 0.18
      card.oy += (ty - card.oy) * 0.18
      if (Math.abs(card.ox) < 0.05) card.ox = 0
      if (Math.abs(card.oy) < 0.05) card.oy = 0

      if (card.ox !== card.lastX || card.oy !== card.lastY) {
        card.lastX = card.ox
        card.lastY = card.oy
        card.el.style.transform = card.ox || card.oy ? `translate(${card.ox.toFixed(2)}px, ${card.oy.toFixed(2)}px)` : ""
      }
    }

    this.rafId = this.active ? requestAnimationFrame(this.frame) : null
  }

  // Random position bleeding past all edges, but not overlapping the center rect.
  position() {
    const c = this.CENTER
    let x, y
    do {
      x = -8 + Math.random() * 104
      y = -5 + Math.random() * 100
    } while (x < c.x1 && x + this.CARD_W > c.x0 && y < c.y1 && y + this.CARD_H > c.y0)
    return [x, y]
  }
}
